// src/utils/sound.ts

// Create an AudioContext lazily so it respects browser autoplay policies
let audioCtx: AudioContext | null = null;

export const playBeep = () => {
  try {
    if (typeof window === 'undefined') return;
    
    if (!audioCtx) {
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioContextClass) return; // Not supported
      audioCtx = new AudioContextClass();
    }
    
    // Resume context if it was suspended (often required by browsers before playing audio)
    if (audioCtx.state === 'suspended') {
      audioCtx.resume();
    }

    const oscillator = audioCtx.createOscillator();
    const gainNode = audioCtx.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioCtx.destination);

    // Beep properties (like a POS scanner beep)
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(1046.50, audioCtx.currentTime); // C6 note (classic beep freq)

    // Volume envelope to avoid clicking sounds
    gainNode.gain.setValueAtTime(0, audioCtx.currentTime);
    gainNode.gain.linearRampToValueAtTime(0.2, audioCtx.currentTime + 0.01); // Quick fade in
    gainNode.gain.setValueAtTime(0.2, audioCtx.currentTime + 0.08); // Hold
    gainNode.gain.linearRampToValueAtTime(0, audioCtx.currentTime + 0.1); // Quick fade out

    oscillator.start(audioCtx.currentTime);
    oscillator.stop(audioCtx.currentTime + 0.1); // 100ms beep length
  } catch (err) {
    console.warn('Audio playback failed', err);
  }
};

export const playSuccessSound = () => {
  try {
    if (typeof window === 'undefined') return;
    
    if (!audioCtx) {
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioContextClass) return;
      audioCtx = new AudioContextClass();
    }
    
    if (audioCtx.state === 'suspended') {
      audioCtx.resume();
    }

    const t = audioCtx.currentTime;
    
    // Play a nice upward chime (e.g., C5, E5, G5)
    const playNote = (freq: number, startTime: number, duration: number) => {
      if (!audioCtx) return;
      const osc = audioCtx.createOscillator();
      const gain = audioCtx.createGain();
      
      osc.connect(gain);
      gain.connect(audioCtx.destination);
      
      osc.type = 'sine';
      osc.frequency.value = freq;
      
      gain.gain.setValueAtTime(0, startTime);
      gain.gain.linearRampToValueAtTime(0.2, startTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
      
      osc.start(startTime);
      osc.stop(startTime + duration);
    };

    playNote(523.25, t, 0.15);       // C5
    playNote(659.25, t + 0.1, 0.15); // E5
    playNote(783.99, t + 0.2, 0.3);  // G5
  } catch (err) {
    console.warn('Audio playback failed', err);
  }
};

export const playErrorSound = () => {
  try {
    if (typeof window === 'undefined') return;
    
    if (!audioCtx) {
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioContextClass) return;
      audioCtx = new AudioContextClass();
    }
    
    if (audioCtx.state === 'suspended') {
      audioCtx.resume();
    }

    const t = audioCtx.currentTime;
    
    // Play a low, descending buzz
    const osc = audioCtx.createOscillator();
    const gain = audioCtx.createGain();
    
    osc.connect(gain);
    gain.connect(audioCtx.destination);
    
    osc.type = 'sawtooth';
    
    // Drop frequency from C4 down to F3 quickly
    osc.frequency.setValueAtTime(261.63, t); // C4
    osc.frequency.exponentialRampToValueAtTime(174.61, t + 0.3); // F3
    
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(0.2, t + 0.05);
    gain.gain.linearRampToValueAtTime(0, t + 0.3);
    
    osc.start(t);
    osc.stop(t + 0.3);
  } catch (err) {
    console.warn('Audio playback failed', err);
  }
};
